import React, { useEffect, useState } from "react";
import FoodShell from "../../layouts/FoodShell";
import { appModules } from "../../components/global";
import { GET, PUT, alert, loading } from "../../components/CustomComponent";
import { Panel, Section, KpiStrip, Chip, Skeleton, thaiDateOf } from "./FoodUi";

const MEAL_LABELS = { breakfast: "เช้า", lunch: "เที่ยง", dinner: "เย็น" };

// งบอาหาร: ยอดใช้จ่ายมาจากราคาในมื้อที่บันทึก (อัตโนมัติ) ผู้ใช้ตั้งแค่งบต่อวัน/เดือน และแบ่งงบต่อมื้อ
export default function FoodBudget() {
  const { title, description } = appModules["food_budget"];
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ daily_budget: "", monthly_budget: "", plan: {} });
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const result = await GET("v1/food/budget");
    if (result?.status === true) {
      setData(result.data);
      setForm({ daily_budget: result.data.daily_budget || "", monthly_budget: result.data.monthly_budget || "", plan: Object.fromEntries(result.data.today.plan.map((item) => [item.key, item.amount])) });
      setDirty(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    loading(true);
    const result = await PUT("v1/food/budget", { daily_budget: Number(form.daily_budget) || 0, monthly_budget: Number(form.monthly_budget) || 0, budget_plan: Object.fromEntries(Object.entries(form.plan).map(([key, value]) => [key, Number(value) || 0])) });
    loading(false);
    if (result?.status === true) {
      setData(result.data);
      setDirty(false);
      alert("", "success", "บันทึกแล้ว", "ระบบจะใช้งบนี้กรองเมนูที่แนะนำ และสลับไปโหมดประหยัดให้เมื่อใช้เร็วกว่าแผน");
    }
  };

  const set = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
  };
  const setPlan = (key, value) => {
    setForm((current) => ({ ...current, plan: { ...current.plan, [key]: value } }));
    setDirty(true);
  };

  const daily = Number(form.daily_budget) || 0;
  const monthly = Number(form.monthly_budget) || 0;
  const spentToday = data?.today?.spent || 0;
  const leftToday = daily - spentToday;
  const planned = Object.values(form.plan).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const monthSpent = data?.month_spent || 0;
  const monthPercent = monthly ? Math.min(100, Math.round((monthSpent / monthly) * 100)) : 0;
  const pacePercent = data ? Math.round((data.days_passed / data.days_in_month) * 100) : 0;
  const perDayLeft = data && data.days_left > 0 ? Math.max(0, Math.floor((monthly - monthSpent) / data.days_left)) : 0;
  const maxHistory = Math.max(...(data?.history || []).map((item) => item.spent), daily, 1);

  return (
    <FoodShell title={title} description={description}>
      <KpiStrip
        className="mb-3"
        items={[
          ["gold", "fa-solid fa-wallet", daily ? `฿${daily}` : "-", "งบวันนี้"],
          ["orange", "fa-solid fa-receipt", `฿${spentToday}`, "ใช้ไปแล้ววันนี้ (จากมื้อที่บันทึก)"],
          [daily ? (leftToday >= 0 ? "green" : "red") : "muted", "fa-solid fa-piggy-bank", daily ? `฿${leftToday}` : "-", daily ? (leftToday >= 0 ? "เหลือวันนี้" : "เกินงบวันนี้") : "ยังไม่ตั้งงบ"],
          ["blue", "fa-solid fa-calendar", monthly ? `฿${perDayLeft}` : "-", data ? `ใช้ได้ต่อวัน (อีก ${data.days_left} วัน)` : "ใช้ได้ต่อวัน"],
        ]}
      />

      <div className="row g-3">
        <div className="col-xl-7">
          <Panel
            eyebrow="Daily plan"
            icon="fa-solid fa-utensils"
            title="แผนงบวันนี้"
            desc="ระบบแบ่งงบตามมื้อให้ (20 / 35 / 45%) ปรับเองได้ เมนูที่แนะนำจะเลือกให้ไม่เกินงบต่อมื้อ"
            actions={
              <>
                <div className="food-inline-input">
                  <span>งบต่อวัน</span>
                  <input type="number" className="form-control" value={form.daily_budget} placeholder="300" onChange={(event) => set("daily_budget", event.target.value)} />
                  <span>บาท</span>
                </div>
                <button type="button" className="btn stg-btn stg-btn--primary" disabled={!dirty} onClick={save}><i className="fa-solid fa-floppy-disk" />บันทึกงบ</button>
              </>
            }
          >
            {!data ? <Skeleton lines={5} /> : (
              <>
                <div className="d-grid gap-2">
                  {data.today.plan.map((item) => {
                    const amount = Number(form.plan[item.key]) || 0;
                    const percent = amount ? Math.min(100, Math.round((item.spent / amount) * 100)) : item.spent ? 100 : 0;
                    return (
                      <div key={item.key} className={`food-budget-row ${item.count ? "is-done" : ""}`}>
                        <div className="food-budget-meal">
                          <span className="food-budget-label">{MEAL_LABELS[item.key]}</span>
                          <div className="food-inline-input food-inline-input--sm">
                            <input type="number" className="form-control" value={form.plan[item.key] ?? ""} onChange={(event) => setPlan(item.key, event.target.value)} />
                            <span>บาท</span>
                          </div>
                        </div>
                        <div className="food-budget-food">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="food-budget-track"><span style={{ width: `${percent}%` }} className={item.spent > amount ? "is-over" : ""} /></div>
                            <div className="food-budget-note">
                              {item.count ? <>ใช้ไป <strong>฿{item.spent}</strong> จาก ฿{amount} ({item.count} รายการ){item.spent > amount ? <Chip tone="red" className="ms-2">เกิน ฿{item.spent - amount}</Chip> : <Chip tone="green" className="ms-2">เหลือ ฿{amount - item.spent}</Chip>}</> : <>ยังไม่ได้กิน · ระบบจะแนะนำเมนูไม่เกิน ฿{amount}</>}
                            </div>
                          </div>
                          {item.count ? <span className="stg-pill stg-pill--success stg-pill--plain"><i className="fa-solid fa-check" /> กินแล้ว</span> : <span className="stg-pill stg-pill--muted stg-pill--plain">รอมื้อนี้</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="food-budget-sum">
                  <span>รวมแผน <strong>฿{planned}</strong> จากงบ ฿{daily}{data.today.other_spent ? ` · ของว่าง/มื้อดึกวันนี้ ฿${data.today.other_spent}` : ""}</span>
                  <span className={daily - planned < 0 ? "text-danger" : ""}>{daily - planned >= 0 ? `เหลือ ฿${daily - planned} ไม่ได้จัดสรร` : `เกินงบ ฿${planned - daily}`}</span>
                </div>
              </>
            )}
          </Panel>
        </div>

        <div className="col-xl-5">
          <Section icon="fa-solid fa-calendar-days" title="งบอาหารรายเดือน" desc="ระบบรวมค่าใช้จ่ายจากมื้อที่บันทึกให้อัตโนมัติ และบอกว่าควรใช้ต่อวันเท่าไหร่ถึงจะพอ" className="mb-3">
            <div className="food-inline-input mb-3">
              <span>งบต่อเดือน</span>
              <input type="number" className="form-control" value={form.monthly_budget} placeholder="9000" onChange={(event) => set("monthly_budget", event.target.value)} />
              <span>บาท</span>
            </div>
            {!data ? <Skeleton /> : (
              <div className="food-month">
                <div className="food-month-value">฿{monthSpent.toLocaleString()} <small>/ ฿{monthly.toLocaleString()}</small></div>
                <div className="food-month-track">
                  <span className="food-month-fill" style={{ width: `${monthPercent}%` }} />
                  <span className="food-month-pace" style={{ left: `${pacePercent}%` }} title={`วันที่ ${data.days_passed} ของเดือน`} />
                </div>
                <div className="food-month-legend">
                  <span>ใช้ไป {monthPercent}%</span>
                  <span>ผ่านมาแล้ว {pacePercent}% ของเดือน</span>
                </div>
                {monthly ? (
                  <p className={`food-tip mt-3 ${monthPercent > pacePercent ? "food-tip--warn" : ""}`}>
                    <i className="fa-solid fa-robot" />
                    {monthPercent > pacePercent
                      ? <>ใช้เร็วกว่าแผน อีก {data.days_left} วันที่เหลือควรใช้ไม่เกิน <strong>฿{perDayLeft}/วัน</strong> ระบบจะเน้นแนะนำเมนูโหมดประหยัดให้</>
                      : <>ยังอยู่ในแผน อีก {data.days_left} วันที่เหลือใช้ได้ <strong>฿{perDayLeft}/วัน</strong></>}
                  </p>
                ) : <p className="food-tip mt-3"><i className="fa-solid fa-robot" />ตั้งงบต่อเดือน ระบบจะติดตามให้และเตือนเมื่อใช้เร็วกว่าแผน</p>}
              </div>
            )}
          </Section>

          <Section icon="fa-solid fa-chart-column" title="7 วันล่าสุด" desc={daily ? `ค่าใช้จ่ายอาหารต่อวัน เทียบกับงบ ฿${daily}` : "ค่าใช้จ่ายอาหารต่อวัน"}>
            {!data ? <Skeleton /> : (
              <div className="food-bars">
                {data.history.map((item) => (
                  <div key={item.date} className="food-bar">
                    <span className="food-bar-value">฿{item.spent}</span>
                    <span className="food-bar-track">
                      <span className={`food-bar-fill ${daily && item.spent > daily ? "is-over" : ""}`} style={{ height: `${Math.round((item.spent / maxHistory) * 100)}%` }} />
                    </span>
                    <span className="food-bar-label">{item.date === data.today.date ? "วันนี้" : thaiDateOf(item.date, "day")}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </FoodShell>
  );
}
